<template>
  <div class="ledger-page">
    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ isEdit ? $t('ledger_ct_edit') : $t('ledger_ct_new') }}
      </h4>
      <b-alert
        v-if="termsLocked"
        show
        variant="info"
      >
        {{ $t('ledger_ct_terms_locked') }}
      </b-alert>
      <b-row>
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group :label="$t('ledger_payroll_project')">
            <v-select
              :value="projectOption"
              :dir="isRtl ? 'rtl' : 'ltr'"
              :options="projectOptions"
              label="label"
              :disabled="isEdit"
              :filterable="false"
              :placeholder="$t('ledger_payroll_project')"
              class="ledger-account-select"
              @search="searchProjects"
              @input="onProject"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_ct_number')">
            <b-form-input v-model="form.contract_number" />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_ct_start')">
            <b-form-input
              v-model="form.start_date"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_ct_value')">
            <b-form-input
              v-model.number="form.contract_value"
              type="number"
              min="0"
              step="0.01"
              :disabled="termsLocked"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_ct_retention_pct')">
            <b-form-input
              v-model.number="form.retention_percent"
              type="number"
              min="0"
              max="99.99"
              step="0.01"
              :disabled="termsLocked"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_ct_estimate')">
            <b-form-input
              v-model.number="form.estimated_total_cost"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group :label="$t('ledger_assets_notes')">
            <b-form-input v-model="form.notes" />
          </b-form-group>
        </b-col>
      </b-row>
      <p class="text-muted small">
        {{ $t('ledger_ct_estimate_edit') }}
      </p>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving || !canSave"
        @click="save"
      >
        {{ $t('ledger_assets_save') }}
      </b-button>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BButton, BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Projects from '@/api/projects/projects'
import { createContract, getContract, updateContract } from '@/api/accounting/contracts'
import {
  listFrom, oneFrom, roundMoney, todayIso, apiErrorMessage, ledgerMessageKey,
} from '@/utils/ledger'

const projectsApi = new Projects()

function projectHasContract(project) {
  if (!project) return false
  if (project.contract_id || project.project_contract_id) return true
  if (project.contract && project.contract.id) return true
  return project.has_contract === true || project.has_contract === 1
}

function projectOptionLabel(project) {
  const service = project.service_type || project.serviceType || {}
  const activity = service.effective_gl_activity || project.effective_gl_activity || {}
  return [project.project_number || project.id, project.name || '', activity.label_ar || '']
    .filter(part => part !== '' && part != null)
    .join(' — ')
}

export default {
  name: 'ContractForm',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BButton, BAlert, vSelect,
  },
  data() {
    return {
      saving: false,
      termsLocked: false,
      projectOptions: [],
      projectOption: null,
      form: {
        project_id: null,
        contract_number: '',
        contract_value: null,
        retention_percent: 0,
        estimated_total_cost: null,
        start_date: todayIso(),
        notes: '',
      },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    canSave() {
      if (!this.isEdit && !this.form.project_id) return false
      if (!(Number(this.form.contract_value) > 0)) return false
      const pct = Number(this.form.retention_percent)
      if (!Number.isFinite(pct) || pct < 0 || pct >= 100) return false
      return true
    },
  },
  mounted() {
    if (this.isEdit) this.load()
  },
  methods: {
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    searchProjects(search, loading) {
      const q = (search || '').trim()
      if (q.length < 1) return
      if (loading) loading(true)
      clearTimeout(this.projectTimer)
      this.projectTimer = setTimeout(async () => {
        try {
          const res = await projectsApi.listProjects({ search: q, per_page: 20, page: 1 })
          this.projectOptions = listFrom(res)
            .filter(project => !projectHasContract(project))
            .map(project => ({
              id: project.id,
              label: projectOptionLabel(project),
              raw: project,
            }))
        } catch (error) {
          this.projectOptions = []
        } finally {
          if (loading) loading(false)
        }
      }, 300)
    },
    onProject(option) {
      this.projectOption = option
      this.form.project_id = option ? option.id : null
    },
    payload() {
      const body = {
        estimated_total_cost: this.form.estimated_total_cost === '' || this.form.estimated_total_cost == null
          ? undefined
          : roundMoney(this.form.estimated_total_cost),
        start_date: this.form.start_date || undefined,
        notes: String(this.form.notes || '').trim() || undefined,
      }
      const number = String(this.form.contract_number || '').trim()
      if (number) body.contract_number = number
      if (!this.termsLocked) {
        body.contract_value = roundMoney(this.form.contract_value)
        body.retention_percent = Number(this.form.retention_percent) || 0
      }
      if (!this.isEdit) body.project_id = this.form.project_id
      return body
    },
    async load() {
      try {
        const res = await getContract(this.$route.params.id)
        const contract = oneFrom(res)
        this.termsLocked = Boolean(contract.position && contract.position.terms_locked)
        this.form.contract_number = contract.contract_number || ''
        this.form.contract_value = Number(contract.contract_value)
        this.form.retention_percent = Number(contract.retention_percent) || 0
        this.form.estimated_total_cost = contract.estimated_total_cost == null ? null : Number(contract.estimated_total_cost)
        this.form.start_date = contract.start_date || ''
        this.form.notes = contract.notes || ''
        this.form.project_id = contract.project_id
        if (contract.project) {
          this.projectOption = {
            id: contract.project.id,
            label: projectOptionLabel(contract.project),
            raw: contract.project,
          }
        }
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
    async save() {
      if (!this.canSave || this.saving) return
      this.saving = true
      try {
        const res = this.isEdit
          ? await updateContract(this.$route.params.id, this.payload())
          : await createContract(this.payload())
        const contract = oneFrom(res)
        this.toast(this.$t('ledger_ct_saved'), 'success')
        const id = (contract && contract.id) || this.$route.params.id
        if (id) this.$router.push({ name: 'ledger-contracts-view', params: { id } })
      } catch (error) {
        const message = apiErrorMessage(error, this.$t('ledger_error'))
        const key = ledgerMessageKey(message)
        this.toast(key ? this.$t(key) : message, error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
